import { WebGLAssets } from "./webglAssets";

/**
 * Audio-Layer-Wiedergabe fuer den READER (Jans Komplex-Scrolly 01.08.2026:
 * "Audio funktioniert nicht" — der Viewer kannte den Layer-Typ gar nicht).
 *
 * Faithful-Port von Jans 1.0 AudioRenderer (pro-editor .../AudioRenderer.tsx),
 * reduziert um die Editor-Eigenheiten: kein Scrub-/Stop-Knopf-Handling, keine
 * IndexedDB-Aufloesung (veroeffentlichte Scrollys tragen echte URLs). Der
 * EDITOR spielt Audio weiterhin ueber seinen eigenen AudioRenderer — dieses
 * Modul wird in renderLayer nur im Reader betreten (editorMode-Guard),
 * sonst liefe der Ton doppelt.
 *
 * Autoplay: der Viewer verlangt einen START-Klick (User-Geste) bevor der
 * Scrolly laeuft — nachfolgende play()-Aufrufe sind damit erlaubt. Ein
 * dennoch abgelehntes play() wird geschluckt (catch), naechster Frame
 * probiert erneut.
 */
export interface AudioLayerContent {
    url?: string;
    loop?: boolean;
    muted?: boolean;
    volume?: number;
    // Fade-Zeiten in ms (Wanduhr, wie 1.0)
    fadeIn?: number;
    fadeOut?: number;
    // zerstoerungsfreies Trim-Fenster in Sekunden
    trimStart?: number;
    trimEnd?: number;
}

interface AudioState {
    element: HTMLAudioElement;
    url: string;
    fadeRaf: number | null;
    stopping: boolean;
    starting: boolean;
    /** Kreuzblende beim Schleifen-Neustart: der NAECHSTE Start blendet so
     *  lange ein (ms) — nur, wenn er bald danach kommt (s. ueberblenden). */
    einblendenMs?: number;
    einblendenBis?: number;
}

export class AudioLayers {
    private states: { [layerId: string]: AudioState } = {};

    private getState(layerId: string, content: AudioLayerContent): AudioState | null {
        if (!content.url) return null;
        let state = this.states[layerId];
        if (!state || state.url !== content.url) {
            if (state) {
                state.element.pause();
                state.element.src = "";
                WebGLAssets.unregisterMediaElement?.(state.element);
            }
            const element = new Audio(content.url);
            element.preload = "auto";
            // an der zentralen Medien-Steuerung anmelden, falls vorhanden
            // (Reader: Hook null — dann einfach freistehend)
            WebGLAssets.registerMediaElement?.(element);
            state = {
                element,
                url: content.url,
                fadeRaf: null,
                stopping: false,
                starting: false,
            };
            this.states[layerId] = state;
        }
        return state;
    }

    private startFade(state: AudioState, from: number, to: number, duration: number, onDone?: () => void) {
        if (state.fadeRaf) cancelAnimationFrame(state.fadeRaf);
        if (duration <= 0) {
            state.element.volume = to;
            onDone?.();
            return;
        }
        const startTime = performance.now();
        const tick = () => {
            const progress = Math.min((performance.now() - startTime) / duration, 1);
            state.element.volume = Math.min(Math.max(from + (to - from) * progress, 0), 1);
            if (progress < 1) {
                state.fadeRaf = requestAnimationFrame(tick);
            } else {
                state.fadeRaf = null;
                onDone?.();
            }
        };
        state.fadeRaf = requestAnimationFrame(tick);
    }

    /**
     * WEICHE TON-UEBERBLENDUNG BEIM SCHLEIFEN-NEUSTART (Jan 17.10.2026, zur
     * Bild-Blende in viewer/schleifenUeberblendung): Bisher stoppte der
     * Ruecksprung durch Timer 0 den Ton (Not-Ausklang max. 400 ms, ohne
     * fadeOut sogar hart) und startete ihn danach neu. Jetzt echte
     * Kreuzblende: das laufende Element klingt losgeloest ueber `dauerMs`
     * aus, ein frisches Element derselben Datei startet beim naechsten
     * Frame im Fenster von vorn und blendet in derselben Zeit ein.
     */
    ueberblenden(dauerMs: number) {
        for (const state of Object.values(this.states)) {
            const alt = state.element;
            if (alt.paused || state.stopping) continue;
            if (state.fadeRaf) cancelAnimationFrame(state.fadeRaf);
            state.fadeRaf = null;
            state.starting = false;
            // altes Element ausklingen lassen, danach freigeben
            const von = alt.volume;
            const start = performance.now();
            const tick = () => {
                const p = Math.min((performance.now() - start) / dauerMs, 1);
                alt.volume = Math.max(von * (1 - p), 0);
                if (p < 1 && !alt.paused) {
                    requestAnimationFrame(tick);
                } else {
                    alt.pause();
                    alt.src = "";
                    WebGLAssets.unregisterMediaElement?.(alt);
                }
            };
            requestAnimationFrame(tick);
            // frisches Element fuer die neue Runde
            const neu = new Audio(state.url);
            neu.preload = "auto";
            WebGLAssets.registerMediaElement?.(neu);
            state.element = neu;
            state.einblendenMs = dauerMs;
            state.einblendenBis = performance.now() + 2000;
        }
    }

    /** Pro Frame aus renderLayer: Lebenszeit-Fenster rein/raus wie Video. */
    update(layerId: string, content: AudioLayerContent, isInLifetime: boolean, timer: number) {
        if (WebGLAssets.isMediaSuppressed?.()) return;
        // STILLE AUFNAHME (Jans Fund 12.10.2026, „Audio spielt, obwohl es
        // nicht aktiv sein sollte"): Kachel-Fotos rendern an FREMDEN
        // Slide-Timern (captureRendererFrame setzt stillCapture) — der Video-
        // Pfad prueft die Flagge laengst, der Ton startete hier trotzdem und
        // klang nach der Aufnahme als Fade-Schwanz nach.
        if (WebGLAssets.stillCapture) return;
        // Im versteckten Tab nichts starten (gleicher Wettlauf wie beim
        // Video, s. Video.play(): ein Nachzuegler-Frame nach visibilitychange
        // wuerde sonst Ton anwerfen, den kein Frame mehr aufraeumt).
        if (typeof document !== "undefined" && document.hidden) return;
        const state = this.getState(layerId, content);
        if (!state) return;

        const { element } = state;
        const targetVolume = content.muted ? 0 : Math.min(Math.max(content.volume ?? 1, 0), 1);
        element.loop = !!content.loop;

        // ganz am Anfang (Scroll auf 0 zurueck) darf nie Ton laufen — aber
        // WEICH (Jans Fund 31.08.2026, "Let it happen"/Wiedergabe-Loop): Beim
        // Loop-Umbruch springt der Timer durch 0, und der harte Sofort-Stopp
        // hackte den gerade laufenden fadeOut mitten durch (gemessen: Schnitt
        // bei 75 % Lautstaerke) — das war Jans "Ton endet hart".
        //   - Laeuft schon ein Ausblende-Fade (stopping): zu Ende klingen
        //     lassen, er pausiert selbst.
        //   - Spielt der Ton ohne Fade: kurzer Not-Ausklang (max. 400 ms)
        //     statt Knall — die Cover-Garantie bleibt (binnen < 0,5 s still).
        if (timer <= 0) {
            if (!element.paused && !state.stopping) {
                state.stopping = true;
                this.startFade(state, element.volume, 0, Math.min(content.fadeOut ?? 0, 400), () => {
                    element.pause();
                    element.currentTime = content.trimStart ?? 0;
                    state.stopping = false;
                });
            }
            return;
        }

        // von selbst zu Ende gelaufen (kein Loop) — erst wieder starten, wenn
        // das Fenster verlassen und neu betreten wurde
        const stoppedAtTrimEnd =
            content.trimEnd !== undefined && element.currentTime >= content.trimEnd - 0.01;

        if (
            isInLifetime &&
            element.paused &&
            !state.stopping &&
            !state.starting &&
            !element.ended &&
            !stoppedAtTrimEnd
        ) {
            state.starting = true;
            // Kreuzblende (ueberblenden): gleich nach dem Schleifen-Neustart
            // mindestens so lange einblenden, wie das alte Element ausklingt
            const kreuz =
                state.einblendenMs !== undefined && performance.now() <= (state.einblendenBis ?? 0)
                    ? state.einblendenMs
                    : 0;
            state.einblendenMs = undefined;
            const fadeIn = Math.max(content.fadeIn ?? 0, kreuz);
            element.currentTime = content.trimStart ?? 0;
            element.volume = fadeIn > 0 ? 0 : targetVolume;
            element
                .play()
                .then(() => {
                    state.starting = false;
                    this.startFade(state, 0, targetVolume, fadeIn);
                })
                .catch(() => {
                    // Autoplay ggf. noch blockiert — naechster Frame versucht es neu
                    state.starting = false;
                });
        } else if (!isInLifetime && !element.paused && !state.stopping) {
            state.stopping = true;
            this.startFade(state, element.volume, 0, content.fadeOut ?? 0, () => {
                element.pause();
                element.currentTime = content.trimStart ?? 0;
                state.stopping = false;
            });
        } else if (!isInLifetime && (element.ended || (element.paused && stoppedAtTrimEnd))) {
            // Ended-/Trim-Zustand loeschen, damit erneutes Betreten neu abspielt
            element.currentTime = content.trimStart ?? 0;
        } else if (isInLifetime && !element.paused && !state.fadeRaf && !state.starting) {
            // Live-Lautstaerke (z. B. muted im JSON) ohne laufenden Fade
            element.volume = targetVolume;
        }

        // zerstoerungsfreies Trim: am Trim-ENDE stoppen bzw. loopen (Jans 1.0)
        if (!element.paused && content.trimEnd !== undefined && element.currentTime >= content.trimEnd) {
            if (content.loop) {
                element.currentTime = content.trimStart ?? 0;
            } else {
                element.pause();
                state.stopping = false;
            }
        }
        // native Dauerschleife springt auf 0 — zurueck ins Trim-Fenster holen
        if (
            !element.paused &&
            content.loop &&
            (content.trimStart ?? 0) > 0 &&
            element.currentTime < (content.trimStart ?? 0) - 0.05
        ) {
            element.currentTime = content.trimStart ?? 0;
        }
    }

    /**
     * Tab versteckt (visibilitychange): alle Audio-Layer anhalten — ein beim
     * Wechsel gerade laufender Ton liefe sonst im Hintergrund-Tab endlos
     * weiter (RAF steht, update() kommt nicht mehr). Beim Zurueckkommen
     * startet der naechste Frame regulaer neu, wenn das Fenster noch passt.
     */
    pauseAll() {
        for (const state of Object.values(this.states)) {
            if (state.fadeRaf) cancelAnimationFrame(state.fadeRaf);
            state.fadeRaf = null;
            state.stopping = false;
            state.starting = false;
            state.element.pause();
        }
    }

    /** Renderer-stop(): alles anhalten und freigeben. */
    dispose() {
        for (const state of Object.values(this.states)) {
            if (state.fadeRaf) cancelAnimationFrame(state.fadeRaf);
            state.element.pause();
            state.element.src = "";
            WebGLAssets.unregisterMediaElement?.(state.element);
        }
        this.states = {};
    }
}
